import api from "../config/api";
import InterviewState, {
  InterviewAnswer,
  InterviewOp,
  InterviewSummary,
  InterviewFocusedGap,
} from "../types/Interview";

// Cliente da Entrevista de autoria (05 §10). Um envelope para todas as ações:
// toda resposta bem-sucedida é o `InterviewState` inteiro e a tela redesenha.
//
// O axios global fixa `Content-Type: application/x-www-form-urlencoded,
// multipart/form-data` (src/config/api.ts:9), o que achata objeto aninhado em
// `chave[sub]=valor`. O Rascunho é aninhado em três níveis (checklist ->
// habilidades -> itens -> detecção) e não sobrevive a isso, então toda chamada
// com corpo manda JSON de verdade.
const AS_JSON = { headers: { "Content-Type": "application/json" } };

/**
 * O interceptor de `config/api.ts` rejeita em erro HTTP, então 409 e 422 caem
 * no `catch`. O 409 não é falha: o corpo já traz o estado atual do servidor —
 * é como duas abas se reconciliam sem uma segunda chamada. Por isso o retorno
 * é discriminado, e não uma promise que estoura: quem chama não tem como
 * confundir conflito com sucesso, nem erro com sucesso.
 */
export type InterviewOutcome =
  | { kind: "ok"; state: InterviewState }
  | { kind: "conflict"; state: InterviewState; msg: string }
  | { kind: "failed"; status: number; msg: string };

/** Quais motores o Aprofundamento deve ouvir. Ausente = comparativo inteiro. */
export type EnrichEngine = "gemini" | "medgemma";

const FALHA_REDE =
  "Não consegui falar com o servidor. Nada foi alterado no Background IA.";

const mensagem = (e: any, padrao: string): string => {
  const corpo = e?.response?.data;
  const detalhe = corpo?.Error?.message;
  if (Array.isArray(detalhe) && detalhe.length > 0) return detalhe.join(" ");
  if (typeof detalhe === "string" && detalhe) return detalhe;
  if (typeof corpo?.Msg === "string" && corpo.Msg) return corpo.Msg;
  if (typeof corpo?.message === "string" && corpo.message) return corpo.message;
  return padrao;
};

const resolver = (e: any, padrao: string, falhaRede = FALHA_REDE): InterviewOutcome => {
  const status = e?.response?.status;
  // Falha de rede, timeout ou CORS: não há `response`, e não há estado novo.
  if (!status) return { kind: "failed", status: 0, msg: falhaRede };
  const corpo = e?.response?.data;
  if (status === 409 && corpo?.interview_id) {
    return { kind: "conflict", state: corpo as InterviewState, msg: mensagem(e, padrao) };
  }
  return { kind: "failed", status, msg: mensagem(e, padrao) };
};

/** Abre uma Entrevista nova. Não existe checklist ainda — é ela que o cria. */
const open = async (seed: Record<string, any> = {}): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>("/sheai/interview", { seed }, AS_JSON);
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não consegui abrir o Background IA.");
  }
};

/**
 * Adota um Checklist criado fora da Entrevista (formulário legado). Devolve a
 * Entrevista deste autor para ele — a existente ou uma nova, com o Rascunho
 * lido do banco e `applied.checklist_id` apontando para o Checklist, de modo
 * que aplicar ATUALIZA em vez de criar outro.
 */
const fromChecklist = async (
  checklistId: number
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/from-checklist/${checklistId}`,
      {},
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui abrir este Checklist no Background IA.");
  }
};

/**
 * Adota um Cenário criado fora da Entrevista (formulário legado). Devolve a
 * Entrevista deste autor para ele — a existente ou uma nova, com o Rascunho
 * lido do banco e `applied.scenario_id` apontando para o Cenário, de modo
 * que aplicar ATUALIZA em vez de criar outro.
 */
const fromScenario = async (
  scenarioId: number
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/from-scenario/${scenarioId}`,
      {},
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui abrir este Cenário no Background IA.");
  }
};

/** Leitura pura do estado. */
const show = async (id: string, signal?: AbortSignal): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.get<InterviewState>(`/sheai/interview/${id}`, ...(signal ? [{ signal }] : []));
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não encontrei esse Background IA.");
  }
};

/**
 * Todas as escritas continuam usando o mesmo endpoint e a mesma transação.
 * Este produtor interno aceita as quatro formas do patch; a API pública abaixo
 * separa geração para tornar `phase` obrigatória no TypeScript.
 */
export type InterviewGenerationPhase = "scenario" | "checklist";

const sendTurn = async (
  id: string,
  message: string,
  seenRevision: number,
  answer?: InterviewAnswer,
  ops?: InterviewOp[],
  generate?: string,
  generatePhase?: InterviewGenerationPhase,
  focusedGap?: InterviewFocusedGap
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/turn`,
      {
        message,
        seen_revision: seenRevision,
        ...(answer ? { answer } : {}),
        ...(ops && ops.length > 0 ? { ops } : {}),
        ...(generate ? { generate, generate_phase: generatePhase } : {}),
        ...(focusedGap ? { focused_gap: focusedGap } : {}),
      },
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não consegui aplicar a alteração. Nada foi mudado.");
  }
};

/** Texto livre, Resposta Guiada ou edição manual. */
const turn = (
  id: string,
  message: string,
  seenRevision: number,
  answer?: InterviewAnswer,
  ops?: InterviewOp[],
  focusedGap?: InterviewFocusedGap
): Promise<InterviewOutcome> =>
  sendTurn(id, message, seenRevision, answer, ops, undefined, undefined, focusedGap);

/**
 * Geração por IA: um turno comum, mas sempre com a fase explícita. Cenário e
 * Checklist são transações separadas para que a revisão humana fique no meio.
 */
const generate = (
  id: string,
  message: string,
  seenRevision: number,
  briefing: string,
  phase: InterviewGenerationPhase
): Promise<InterviewOutcome> =>
  sendTurn(id, message, seenRevision, undefined, undefined, briefing, phase);

/** Confirma o Cenário corrente sem reescrever o Rascunho nem avançar revisão. */
const reviewScenario = async (
  id: string,
  seenRevision: number
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/scenario-review`,
      { seen_revision: seenRevision },
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui confirmar a revisão do Cenário.");
  }
};

/** Registra a decisão humana de usar a câmera focal, preservando suas regras. */
const setCamera2Enabled = async (
  id: string,
  enabled: boolean,
  seenRevision: number
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/camera2`,
      { enabled, seen_revision: seenRevision },
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui alterar o uso da câmera focal.");
  }
};

/**
 * Dispensa (ou revoga a dispensa de) uma Lacuna. `waiver` é a string opaca que
 * veio no próprio gap — não montar aqui. Dispensa de P0 o servidor ignora.
 */
const waive = async (
  id: string,
  waiver: string,
  on: boolean
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/waive`,
      { waiver, on },
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não consegui registrar a dispensa.");
  }
};

/**
 * Pede a proposta de Aprofundamento.
 *
 * NÃO manda `seen_revision` de propósito: propor não escreve no Rascunho, e
 * exigir a revisão aqui faria a proposta falhar por um turno que aconteceu em
 * outra aba — sem que houvesse nada a proteger.
 *
 * `focus` é o `waiver` da Lacuna (string opaca vinda do próprio gap);
 * `itemRef` é o foco explícito de revisão das regras de um Item já completo.
 * Sem nenhum dos dois, é varredura do documento inteiro.
 */
const enrich = async (
  id: string,
  focus?: string,
  steer?: string,
  itemRef?: string,
  engines?: EnrichEngine[],
  itemCamera?: "camera1" | "camera2"
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/enrich`,
      {
        ...(focus ? { focus } : {}),
        ...(steer ? { steer } : {}),
        ...(itemRef ? { item_ref: itemRef } : {}),
        ...(itemRef && itemCamera ? { item_camera: itemCamera } : {}),
        ...(engines && engines.length > 0 ? { engines } : {}),
      },
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui pedir o aprofundamento. O Rascunho não mudou.");
  }
};

/**
 * Adota as Emendas marcadas. É turno — por isso `seenRevision`, e por isso
 * pode voltar `conflict` como qualquer outra escrita.
 */
const adoptEnrichment = async (
  id: string,
  amendmentIds: string[],
  seenRevision: number,
  proposalId: string
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/enrich/adopt`,
      {
        amendment_ids: amendmentIds,
        seen_revision: seenRevision,
        proposal_id: proposalId,
      },
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui adotar as melhorias. Nada foi mudado.");
  }
};

/** Joga a proposta fora. O Rascunho não é tocado — ela nunca o tocou. */
const discardEnrichment = async (
  id: string,
  proposalId: string
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.delete<InterviewState>(
      `/sheai/interview/${id}/enrich`,
      { ...AS_JSON, data: { proposal_id: proposalId } }
    );
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui descartar a proposta.");
  }
};

/** Nada a redesenhar: a Entrevista saiu da lista. */
export type DiscardOutcome =
  | { kind: "done" }
  | { kind: "failed"; status: number; msg: string };

/**
 * Descarta uma Entrevista. Exclusão lógica no servidor, reversível no banco.
 *
 * Entrevista já aplicada volta 409: ela é o registro de autoria do Cenário e
 * do Checklist que criou. Descartar remove a CONVERSA, nunca o artefato.
 */
const discard = async (id: string): Promise<DiscardOutcome> => {
  try {
    await api.delete(`/sheai/interview/${id}`);
    return { kind: "done" };
  } catch (e: any) {
    return {
      kind: "failed",
      status: e?.response?.status ?? 0,
      msg: mensagem(e, "Não consegui descartar o Background IA."),
    };
  }
};

/**
 * Duplica: Entrevista nova com o mesmo Rascunho, conversa do zero.
 * O original fica intacto.
 */
const duplicate = async (id: string): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/duplicate`,
      {},
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não consegui duplicar o Background IA.");
  }
};

/**
 * Reabre para editar NO MESMO LUGAR. Volta 409 quando o Checklist já está em
 * uso — publicado, ou já usado para avaliar aluno. Nesse caso a saída é
 * duplicar, e a mensagem do servidor já diz isso.
 */
const reopen = async (id: string): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/reopen`,
      {},
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não consegui reabrir o Background IA.");
  }
};

/** Projeta o Rascunho em Cenário + Checklist (+ Estação). Idempotente. */
const apply = async (id: string): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(
      `/sheai/interview/${id}/apply`,
      {},
      AS_JSON
    );
    return { kind: "ok", state: data };
  } catch (e: any) {
    return resolver(e, "Não consegui aplicar. Nada foi gravado.");
  }
};

export type MineOutcome =
  | { kind: "ok"; items: InterviewSummary[] }
  | { kind: "failed"; status: number; msg: string };

/** As Entrevistas do professor logado. Array cru, sem envelope de paginação. */
const mine = async (): Promise<MineOutcome> => {
  try {
    const { data } = await api.get<InterviewSummary[]>("/sheai/interview");
    return { kind: "ok", items: Array.isArray(data) ? data : [] };
  } catch (e: any) {
    return {
      kind: "failed",
      status: e?.response?.status ?? 0,
      msg: mensagem(e, "Não consegui carregar seus Backgrounds IA."),
    };
  }
};

/** O browser define o boundary; não reutilizar o Content-Type global. */
const importDocuments = async (
  id: string, files: File[], seenRevision: number, requestId: string,
  message = "", signal?: AbortSignal
): Promise<InterviewOutcome> => {
  const body = new FormData();
  files.forEach((file) => body.append("files[]", file));
  body.append("seen_revision", String(seenRevision));
  body.append("request_id", requestId);
  if (message) body.append("message", message);
  try {
    const { data } = await api.post<InterviewState>(`/sheai/interview/${id}/imports`, body, {
      headers: { "Content-Type": undefined }, signal, timeout: 240000,
    });
    return { kind: "ok", state: data };
  } catch (e: unknown) {
    return resolver(e, "Não consegui enviar os arquivos. Confira o estado da importação antes de tentar novamente.",
      "A conexão foi interrompida e não consegui confirmar o envio. Tente novamente para consultar ou retomar a mesma importação.");
  }
};

const chooseImportScenario = async (
  id: string, importId: string, seenRevision: number, candidateId: string
): Promise<InterviewOutcome> => {
  try {
    const { data } = await api.post<InterviewState>(`/sheai/interview/${id}/turn`, {
        message: "", seen_revision: seenRevision,
        import_choice: { import_id: importId, candidate_id: candidateId },
      }, AS_JSON);
    return { kind: "ok", state: data };
  } catch (e: unknown) { return resolver(e, "Não consegui atualizar a importação.", "Não consegui confirmar a ação. Atualize a conversa para conferir o estado salvo da importação."); }
};

const InterviewService = {
  importDocuments,
  chooseImportScenario,
  open,
  fromChecklist,
  fromScenario,
  show,
  turn,
  generate,
  reviewScenario,
  setCamera2Enabled,
  waive,
  apply,
  mine,
  discard,
  duplicate,
  reopen,
  enrich,
  adoptEnrichment,
  discardEnrichment,
};

export default InterviewService;
